import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { ApiError } from "../../../lib/http";
// The module rather than the feature's index: uploading a file is not the brand
// screen's business, and reaching for it through `../../brand` would pull that
// whole screen in behind it.
import { uploadFile } from "../../brand/services/fileService";
import ProfileAvatar from "./ProfileAvatar";
import {
  ACCEPTED_PHOTO_ATTR,
  ACCEPTED_PHOTO_TYPES,
  MAX_PHOTO_BYTES,
} from "../data/profileMock";
import type { ProfilePhoto } from "../types/settings.types";

/** The picked file as a data URL, which is what the card paints. */
const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });

interface ProfilePhotoCardProps {
  photo: ProfilePhoto | null;
  /** What the initial falls back to while there is no picture. */
  name: string;
  onPhotoChange: (photo: ProfilePhoto | null) => void;
}

/**
 * The picture beside the form, and the picker that changes it.
 *
 * Picking a file uploads it there and then, rather than holding the bytes back
 * until Save — the same way the brand screen's `UploadField` works, and for the
 * same reason: the profile is saved as a `public_id`, so the bytes have to have
 * a home before there is an id to save. `POST /api/v1/files/` with a purpose of
 * `PROFILE_PHOTO` is what gives them one.
 *
 * The disc shows the picked file while that call is in flight, so the wait
 * reads as progress on something already chosen. The form is only handed the
 * photo once the backend has taken it, which is what makes Cancel able to put
 * the old picture back with nothing half-uploaded left behind it.
 *
 * The rules it keeps are the ones the hint under it states: JPG, GIF or PNG,
 * and no more than 2MB. A picture past either is turned away here rather than
 * at save time, when it is far too late to pick another.
 *
 * Must be used inside a Clerk provider and an active session — the upload
 * carries a bearer token like every other call.
 */
export const ProfilePhotoCard: React.FC<ProfilePhotoCardProps> = ({
  photo,
  name,
  onPhotoChange,
}) => {
  const { getToken } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  // The picture being uploaded, shown in place of `photo` until it lands.
  const [pending, setPending] = useState<{ dataUrl: string } | null>(null);

  // The upload in flight, so picking again — or leaving the page — drops the
  // one before it rather than letting a slow first call land last.
  const uploadRef = useRef<AbortController | null>(null);
  useEffect(() => () => uploadRef.current?.abort(), []);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      setError("Only JPG and PNG files are allowed");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError("That picture is larger than 2MB");
      return;
    }

    setError("");

    let dataUrl: string;
    try {
      dataUrl = await readDataUrl(file);
    } catch {
      setError("That file could not be read");
      return;
    }

    uploadRef.current?.abort();
    const controller = new AbortController();
    uploadRef.current = controller;
    setPending({ dataUrl });

    try {
      const stored = await uploadFile(() => getToken(), file, "PROFILE_PHOTO", {
        signal: controller.signal,
      });

      // Superseded by a later pick, or unmounted: whatever came back belongs to
      // a card that has moved on.
      if (uploadRef.current !== controller) return;

      onPhotoChange({
        name: file.name,
        // The bytes already in hand, rather than the stored URL — the picture
        // paints at once, and needs no token to fetch.
        dataUrl,
        publicId: stored.publicId,
        url: stored.url,
      });
    } catch (uploadError) {
      if (uploadRef.current !== controller) return;
      setError(
        uploadError instanceof ApiError
          ? uploadError.message
          : "That picture could not be uploaded. Check your connection and try again."
      );
      console.error("PROFILE_PHOTO upload failed:", uploadError);
    } finally {
      if (uploadRef.current === controller) {
        uploadRef.current = null;
        setPending(null);
      }
    }
  };

  const remove = () => {
    // A picture being uploaded is also a picture being removed: dropping the
    // call stops its result arriving after the card was emptied.
    uploadRef.current?.abort();
    uploadRef.current = null;
    setPending(null);
    setError("");
    onPhotoChange(null);
  };

  const busy = pending !== null;
  // The upload in flight wins over what is saved, so the disc shows what was
  // just picked rather than the picture it is about to replace.
  const shown = pending ?? photo;

  return (
    // On a phone the card's chrome comes off and the picture stands on the
    // page tint at the head of the form, the way a phone shows the face a
    // profile belongs to. It is the one thing on this screen that is not a
    // field, and a white box drawn around it there only makes it look like
    // another section with one control in it. From `md` up it is the card the
    // design has beside the form.
    <section className="md:rounded-md md:border md:border-line md:bg-white md:p-5 3xl:p-6">
      {/* Nothing to caption on a phone: the picture is the first thing on the
          screen and says what it is. */}
      <h2 className="hidden text-[13px] 3xl:text-[14px] text-ink-muted md:block">
        Profile Photo
      </h2>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_PHOTO_ATTR}
        aria-label="Profile photo"
        disabled={busy}
        // The same file picked twice in a row is still a change worth acting
        // on — after a rejected pick it is the natural way to retry.
        onClick={(event) => {
          (event.target as HTMLInputElement).value = "";
        }}
        onChange={(event) => void handleFile(event.target.files?.[0])}
        className="sr-only"
      />

      <div className="flex flex-col items-center md:mt-4">
        <div className="relative">
          {/* Smaller on a phone: at 136px the disc and the two controls under
              it take most of a screen before the first field is reached. */}
          <ProfileAvatar
            photo={shown}
            name={name}
            className={`h-24 w-24 md:h-[136px] md:w-[136px] ${
              busy ? "opacity-50" : ""
            }`}
            initialClassName="text-[32px] md:text-[40px]"
          />
          {busy && (
            <span
              // The disc is already showing the picked file, so this only has
              // to say that something is still happening to it.
              className="absolute inset-0 flex items-center justify-center"
              role="status"
            >
              <Loader2
                className="w-6 h-6 animate-spin text-brand-dark"
                aria-hidden="true"
              />
              <span className="sr-only">Uploading your photo</span>
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="mt-4 text-[14px] 3xl:text-[15px] font-medium text-brand-dark hover:underline disabled:opacity-50 disabled:no-underline"
        >
          Change Photo
        </button>

        {/* Only where there is something to remove — a control that does
            nothing is a control that has to be reasoned about. While an upload
            is in flight it is also what cancels it. */}
        {shown && (
          <button
            type="button"
            onClick={remove}
            className="mt-1.5 text-[13px] text-ink-muted hover:text-ink transition-colors"
          >
            {busy ? "Cancel" : "Remove"}
          </button>
        )}

        {error ? (
          <p className="mt-2 text-center text-[11px] 3xl:text-[12px] text-red-500">
            {error}
          </p>
        ) : (
          <p className="mt-2 text-center text-[12px] 3xl:text-[13px] text-ink-subtle">
            JPG and PNG. Max size 2MB
          </p>
        )}
      </div>
    </section>
  );
};

export default ProfilePhotoCard;
